
import { Injectable } from "@angular/core";
import { Action, Selector, State, StateContext, Store } from "@ngxs/store";
import { patch } from "@ngxs/store/operators";
import { of, tap } from "rxjs";

import * as CommonDataActions from '@app/shared/actions/common-data.action';
import { WebApiService } from "@shared/services/web-api.service";
import { WebApiResponse } from "@shared/classes/general";
import { HttpMethodEnum } from "@shared/enums";
import { WebApiRoute } from "@shared/constants";
import { LongRunningTask } from "@shared/decorators/long-running-task.decorator";
import { CommonDataStateModel } from "@shared/models/common-data.model";
import { SubRegion } from "@app/shared/classes/subRegion";
import { Driver } from "@shared/classes/driver";
import { UserProfile } from "@shared/classes/user-profile";
import { UserProfileRole } from "@shared/classes/user-profile-role";
import { NgxsStoreHelpers } from "./store.helpers";
import { Utilities } from "@shared/utils/utilities";

@State<CommonDataStateModel>({
  name: 'commonDataState',
  defaults: {
    profileMe: null,
    isCommonDataLoaded: false
  }
})
@Injectable()
export class CommonDataState {
  constructor(
    private store: Store,
    private webApiService: WebApiService,
  ) { }

  @Selector()
  static profileMe(state: CommonDataStateModel) {
    return state.profileMe;
  }

  @Selector()
  static userCountry(state: CommonDataStateModel) {
    return state.profileMe?.country;
  }

  @Selector()
  static isCommonDataLoaded(state: CommonDataStateModel) {
    return state.isCommonDataLoaded;
  }

  @Action(CommonDataActions.SetIsCommonDataLoaded)
  SetIsCommonDataLoaded(ctx: StateContext<CommonDataStateModel>, action: CommonDataActions.SetIsCommonDataLoaded) {
    ctx.setState(patch({ isCommonDataLoaded: true }));
  }

  @LongRunningTask
  @Action(CommonDataActions.GetUserProfileMe)
  GetUserProfileMe(ctx: StateContext<CommonDataStateModel>, action: CommonDataActions.GetUserProfileMe) {
    var url = `${WebApiRoute.USER_PROFILE_ME}`;
    if (action.hackApexId !== "") {
      url = `${WebApiRoute.USER_PROFILE_SPECIFIED}${action.hackApexId}`;
      return this.webApiService.request<WebApiResponse<UserProfile[]>>(HttpMethodEnum.GET, url).pipe(
        tap(response => NgxsStoreHelpers.SetUserProfileByApexId(ctx, response as WebApiResponse<UserProfile[]>, action.hackApexId)));
    }
    else if (ctx.getState().profileMe !== null) {
      return of();
    }

    return this.webApiService.request<UserProfile>(HttpMethodEnum.GET, url).pipe(
      tap(response => NgxsStoreHelpers.SetUserProfile(ctx, response as UserProfile)));
  }

  @LongRunningTask
  @Action(CommonDataActions.GetOrgTerminalList)
  GetOrgTerminalList(ctx: StateContext<CommonDataStateModel>, action: CommonDataActions.GetOrgTerminalList) {
    if (Utilities.isEmptyString(action.terminalId)) {
      return of([]);
    }

    return this.webApiService.request<[]>(HttpMethodEnum.GET, `${WebApiRoute.ORG_UNIT_TERMINALS}?terminalId=${encodeURIComponent(action.terminalId + "%")}`);
  }

  @LongRunningTask
  @Action(CommonDataActions.GetDriverRelatedTerminalList)
  GetDriverRelatedTerminalList(ctx: StateContext<CommonDataStateModel>, action: CommonDataActions.GetDriverRelatedTerminalList) {
    return this.webApiService.request<[]>(HttpMethodEnum.GET, `${WebApiRoute.DRIVER_TERMINAL}`);
  }

  @LongRunningTask
  @Action(CommonDataActions.GetSubRegionRelatedTerminalList)
  GetSubRegionRelatedTerminalList(ctx: StateContext<CommonDataStateModel>, action: CommonDataActions.GetSubRegionRelatedTerminalList) {
    return this.webApiService.request<[]>(HttpMethodEnum.GET, `${WebApiRoute.ORG_UNIT}`);
  }

  @LongRunningTask
  @Action(CommonDataActions.GetSubRegions)
  GetSubRegions(ctx: StateContext<CommonDataStateModel>, action: CommonDataActions.GetSubRegions) {
    return this.webApiService.request<WebApiResponse<SubRegion[]>>(HttpMethodEnum.GET, `${WebApiRoute.ORG_SUBREGIONS}`);
  }

  @LongRunningTask
  @Action(CommonDataActions.GetUserProfileRole)
  GetUserProfileRole(ctx: StateContext<CommonDataStateModel>, action: CommonDataActions.GetUserProfileRole) {
    return this.webApiService.request<WebApiResponse<UserProfileRole[]>>(HttpMethodEnum.GET, `${WebApiRoute.USER_PROFILE_ROLE}`);
  }

  @LongRunningTask
  @Action(CommonDataActions.GetKpiAll)
  GetKpiAll(ctx: StateContext<CommonDataStateModel>, action: CommonDataActions.GetKpiAll) {
    return this.webApiService.request<[]>(HttpMethodEnum.GET, `${WebApiRoute.KPI}`);
  }

  @LongRunningTask
  @Action(CommonDataActions.SearchDriversWithMyProfile)
  SearchDriversWithMyProfile(ctx: StateContext<CommonDataStateModel>, action: CommonDataActions.SearchDriversWithMyProfile) {
    if (Utilities.isEmptyString(action.driverApexIdOrName)) {
      return of({ data: [] } as WebApiResponse<Driver[]>);
    }

    var profileMe = ctx.getState().profileMe;
    var queryString = this.GenerateMyDriverQueryStringFromMyProfile(profileMe);
    return this.webApiService.request<WebApiResponse<Driver[]>>(HttpMethodEnum.GET, `${WebApiRoute.DRIVER}?drivername=${action.driverApexIdOrName}${queryString}&status=A`);
  }

  @LongRunningTask
  @Action(CommonDataActions.ValidateUserProfile)
  ValidateUserProfile(ctx: StateContext<CommonDataStateModel>, action: CommonDataActions.ValidateUserProfile) {
    if (Utilities.isEmptyString(action.apexID)) {
      return of({} as UserProfile);
    }

    // if(Utilities.isEmptyString(action.apexID)){

    // }

    return this.webApiService.request<UserProfile>(HttpMethodEnum.GET, `${WebApiRoute.USERPROFILE_VALIDATE}/${action.apexID}`);
  }

  private GenerateMyDriverQueryStringFromMyProfile(profileMe: UserProfile) {
    var queryString = "";
    if (profileMe.terminal_ids && profileMe.terminal_ids.length > 0) {
      profileMe.terminal_ids.forEach(terminalId => queryString += `&terminal_id=${terminalId}`);
      return queryString;
    }

    if (profileMe.businessTypes && profileMe.businessTypes.length > 0) {
      profileMe.businessTypes.forEach(businessType => queryString += `&businessType=${businessType}`);
    }

    if (profileMe.subRegion_ids && profileMe.subRegion_ids.length > 0) {
      profileMe.subRegion_ids.forEach(subRegionId => queryString += `&subRegion_Id=${subRegionId}`);
      return queryString;
    }

    if (!Utilities.isEmptyString(profileMe.country)) {
      queryString += `&country=${profileMe.country}`;
    }

    return queryString;
  }
}
